import React, { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { date } from 'utils'

import { useCanAccess } from 'hooks'
import { usePendingSales, type PendingSale, type PendingSaleStatus } from 'hooks/api'
import { ROUTES } from 'routes'
import { PENDING_SALE_STATUSES } from 'constants/statuses'
import { CircleXIcon, HandCoinsIcon, PlusIcon, SearchXIcon } from 'lucide-react'

import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'

import { Button } from '@repo/ui'
import {
  ActionDropdown,
  EmptyState,
  Header,
  IntroDataBanner,
  ProductCell,
  RecordSaleModal,
  StatusChipCell,
  Table,
  VoidSaleModal,
  type ActionDropdownItem,
} from 'components'

const RECORD_SALE_PERMISSION_TOOLTIP =
  "You need the 'Assign to buyer' permission. Ask an admin to grant it."

const PAGE_SIZE = 20

const STATUS_VALUES: PendingSaleStatus[] = ['pending', 'resolved', 'voided']

/**
 * VRG-455 — the sales a brand has recorded against a buyer without naming a
 * certificate.
 *
 * ## Why this is its own view rather than a tab
 *
 * A recorded sale is an object the brand OPERATES on directly: they create it,
 * search it when a buyer writes in, and void it on a refund (§8). A facet of
 * another object would be a tab; this is not one — a sale has no certificate to
 * live under, which is the entire premise of the feature.
 *
 * ## Why it exists at all
 *
 * Void shipped in the API with no way to reach it: a brand could create records
 * and never browse them, so the only caller who could void one was a client
 * that had kept the id from a create response. An action with no discoverable
 * entry point is not an action.
 */
export const SalesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZE })

  const [openRecordModal, setOpenRecordModal] = useState(false)
  const [saleToVoid, setSaleToVoid] = useState<PendingSale | null>(null)

  const { isHasAccess: canRecordSale } = useCanAccess({ action: 'record_sale' })

  const search = searchParams.get('q') || ''
  const statusParam = searchParams.get('status') as PendingSaleStatus | null
  const status = statusParam && STATUS_VALUES.includes(statusParam) ? statusParam : undefined

  const { data, isLoading, refetch } = usePendingSales({
    statuses: status ? [status] : undefined,
    search: search || undefined,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
  })

  const sales = data?.items ?? []
  const total = data?.total ?? 0

  const handlePaginationModelChange = (next: { page: number; pageSize: number }) => {
    // A page-size change resets to the first page: staying on page 4 of a
    // re-paginated list lands somewhere the user did not ask for.
    setPaginationModel(
      next.pageSize !== paginationModel.pageSize
        ? { page: 0, pageSize: next.pageSize }
        : next,
    )
  }

  const getActionItems = useCallback(
    (row: PendingSale): ActionDropdownItem[] => [
      {
        label: 'Void sale',
        onClick: () => setSaleToVoid(row),
        // RECORD STATE hides, permission disables (§46). A resolved record has
        // been claimed — undoing that is a transfer reversal, not a void — and a
        // voided one is already terminal. Neither is a missing capability, so
        // neither gets a tombstone row explaining itself.
        visible: row.status === 'pending',
        // PERMISSION disables, with the sentence that makes it actionable.
        disabled: !canRecordSale,
        tooltip: canRecordSale ? undefined : RECORD_SALE_PERMISSION_TOOLTIP,
        destructive: true,
        icon: <CircleXIcon strokeWidth={1} />,
      },
    ],
    [canRecordSale],
  )

  const columns = useMemo<GridColDef<PendingSale>[]>(
    () => [
      {
        field: 'buyer_email',
        headerName: 'Buyer',
        type: 'string',
        pinned: 'left',
        disableReorder: true,
        // The pinned first column's house width. A long address still ellipsizes,
        // which §49 permits for a CELL value — only the HEADER may never clip.
        width: 280,
        /*
          NO TOOLTIP (removed 2026-09-01, user directive). This cell used to wrap
          the email in a `<Tooltip title={row.buyer_email}>`, i.e. a floating
          surface whose entire content was the text already under the cursor. At
          280px most addresses are not truncated at all, so it fired on nearly
          every hover across a PINNED column — the one you sweep past most — to
          tell you what you were already reading.

          If the truncated case needs recovering, the cheap fix is the native
          `title` attribute rather than reinstating MUI's `Tooltip`: no floating
          surface, no delay, no portal, and the browser shows it only when it is
          worth showing. Not added here because it was not asked for.
        */
        renderCell: ({ row }: GridRenderCellParams<PendingSale>) => (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.buyer_email}
          </span>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'singleSelect',
        valueOptions: STATUS_VALUES,
        // Header ("Status") plus its sort icon plus cell padding. The chip
        // labels are all shorter than the header, so the header is what sizes
        // this (§49).
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <StatusChipCell status={row.status} statuses={PENDING_SALE_STATUSES} />
        ),
      },
      {
        field: 'product',
        headerName: 'Product',
        type: 'string',
        width: 260,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<PendingSale>) =>
          row.product?.id ? (
            // The shared cell, which carries the image plate, the link and the
            // referrer state that gives the product page a "← Back" here (§42).
            // The payload projection has a single `image`; the cell reads the
            // catalog's `images[]`, adapted here rather than special-casing the
            // shared component (same adaptation as the Packages list).
            <ProductCell
              product={{
                ...row.product,
                images: row.product.image ? [row.product.image] : [],
              }}
              route={ROUTES.PRODUCT(row.product.id)}
            />
          ) : (
            '-'
          ),
      },
      {
        field: 'buyer_phone',
        headerName: 'Phone',
        type: 'string',
        width: 170,
        renderCell: ({ row }) => row.buyer_phone || '-',
      },
      {
        field: 'created_at',
        headerName: 'Recorded',
        type: 'dateTime',
        width: 200,
        valueGetter: (value: string) => value && new Date(value),
        renderCell: ({ value }) => (value ? date(value) : '-'),
      },
      {
        field: 'resolved_at',
        headerName: 'Claimed',
        type: 'dateTime',
        width: 200,
        valueGetter: (value: string | null) => value && new Date(value),
        // Blank rather than a dash-with-a-story: a pending record has no claim
        // date because the buyer has not scanned, which the Status column
        // already says.
        renderCell: ({ value }) => (value ? date(value) : '-'),
      },
      {
        field: 'actions',
        headerName: ' ',
        sortable: false,
        align: 'center',
        width: 54,
        pinned: 'right',
        renderCell: ({ row }: GridRenderCellParams<PendingSale>) => (
          <ActionDropdown items={getActionItems(row)} />
        ),
      },
    ],
    [getActionItems],
  )

  const emptyState = useMemo(() => {
    if (total === 0 && !search && !status) {
      return (
        <EmptyState
          title="No sales recorded"
          text="Assign a product to the person who bought it. Their scan works out which item they have and completes the transfer."
          icon={<HandCoinsIcon strokeWidth={1} size={32} />}
        />
      )
    }

    return (
      <EmptyState
        title="No sales found"
        text="No recorded sales match the current filters"
        icon={<SearchXIcon strokeWidth={1} size={32} />}
      />
    )
  }, [total, search, status])

  return (
    <>
      <Header
        title="Sales"
        total={total}
        rightComponents={
          // One primary, rightmost (§26/§33). Default size — never `small` (§60).
          // Permission DISABLES it with the sentence naming what to ask for, it
          // is never hidden: this is an in-context action on a page the user can
          // already see (§27/§46).
          <Button
            onClick={() => setOpenRecordModal(true)}
            startIcon={<PlusIcon strokeWidth={1} />}
            disabled={!canRecordSale}
            disabledReason={canRecordSale ? undefined : RECORD_SALE_PERMISSION_TOOLTIP}
          >
            Assign to buyer
          </Button>
        }
      />

      {/*
        VRG-512: the sample-data caveat, in the same slot Products and Certificates use —
        directly under the `Header`, above the table it qualifies. See
        `IntroDataBanner`'s own docblock for the two states.

        The seed writes sample rows straight into `pending_ownership` fenced by
        `example_kind`, so every row this list can show is either the tenant's own or a
        sample; there is no third source to mis-caption, and no surface condition is
        needed (unlike the internal Orders list).

        The table below is `fillBottom` with no `topOffset`, so nothing needs adjusting:
        `useFillBottom` measures the grid's own top and observes the ancestor chain, so a
        banner appearing above it re-fills rather than overflowing (§22).
      */}
      <IntroDataBanner nouns="sales" />

      <Table
        columns={columns}
        rows={sales}
        // The shared Table renders a column-aware skeleton whenever `loading` is
        // true, so the list gets §19's footprint-parity loading state for free —
        // no separate spinner.
        loading={isLoading}
        // This table IS the page's only flow content, so it fills to a bottom
        // gap matching the 24px left/right gutter (§22).
        fillBottom
        showSearch
        search={search}
        onSearchChange={value => {
          setSearchParams(
            prev => {
              const params = new URLSearchParams(prev)
              if (value) params.set('q', value)
              else params.delete('q')
              return params
            },
            { replace: true },
          )
          setPaginationModel(prev => ({ ...prev, page: 0 }))
        }}
        showFilters
        showSort
        paginationMode="server"
        rowCount={total ?? -1}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        emptyState={emptyState}
        viewKey="sales"
      />

      <RecordSaleModal
        open={openRecordModal}
        onClose={() => setOpenRecordModal(false)}
        refetch={refetch}
        canRecord={canRecordSale}
      />

      <VoidSaleModal
        sale={saleToVoid}
        open={!!saleToVoid}
        onClose={() => setSaleToVoid(null)}
        refetch={refetch}
      />
    </>
  )
}

